import React, { useState, useEffect } from "react";
import { estoque, produtos } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Search, Pencil, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatNumber } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export function EstoquePage() {
  const [data, setData] = useState([]);
  const [produtosList, setProdutosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    estoque_proprio: 0,
    estoque_consignado: 0,
    estoque_importacao: 0,
    estoque_reservado: 0,
  });

  const fetchData = async () => {
    try {
      const [estoqueRes, produtosRes] = await Promise.all([
        estoque.list(),
        produtos.list(),
      ]);
      
      // Merge estoque with produto info
      const mergedData = estoqueRes.data.map((e) => {
        const produto = produtosRes.data.find((p) => p.id === e.produto_id);
        return {
          ...e,
          codigo_interno: produto?.codigo_interno || "-",
          descricao: produto?.descricao || "Produto não encontrado",
          categoria: produto?.categoria || "-",
        };
      });
      
      setData(mergedData);
      setProdutosList(produtosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar estoque");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      estoque_proprio: item.estoque_proprio || 0,
      estoque_consignado: item.estoque_consignado || 0,
      estoque_importacao: item.estoque_importacao || 0,
      estoque_reservado: item.estoque_reservado || 0,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await estoque.update(editingItem.produto_id, formData);
      toast.success("Estoque atualizado!");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar");
    }
  };

  const filteredData = data.filter((item) =>
    item.descricao?.toLowerCase().includes(search.toLowerCase()) ||
    item.codigo_interno?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalProprio = data.reduce((acc, e) => acc + (e.estoque_proprio || 0), 0);
  const totalConsignado = data.reduce((acc, e) => acc + (e.estoque_consignado || 0), 0);
  const totalImportacao = data.reduce((acc, e) => acc + (e.estoque_importacao || 0), 0);
  const criticos = data.filter((e) => (e.estoque_disponivel || 0) < 10).length;

  const columns = [
    { key: "codigo_interno", header: "Código", width: "100px" },
    { key: "descricao", header: "Produto" },
    { key: "categoria", header: "Categoria" },
    { key: "estoque_proprio", header: "Próprio", render: (v) => formatNumber(v) },
    { key: "estoque_consignado", header: "Consignado", render: (v) => formatNumber(v) },
    { key: "estoque_importacao", header: "Importação", render: (v) => formatNumber(v) },
    { key: "estoque_reservado", header: "Reservado", render: (v) => formatNumber(v) },
    {
      key: "estoque_disponivel",
      header: "Disponível",
      render: (v) => (
        <span className={v < 10 ? "text-red-600 font-semibold" : ""}>
          {formatNumber(v)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "60px",
      render: (_, row) =>
        isAdmin() && (
          <Button variant="ghost" size="icon" onClick={() => handleEdit(row)} data-testid={`edit-estoque-${row.id}`}>
            <Pencil className="h-4 w-4" />
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="estoque-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Controle de Estoque</h1>
          <p className="text-muted-foreground">Acompanhamento de níveis de estoque por produto</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />
              Estoque Próprio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold">{formatNumber(totalProprio)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Consignado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold">{formatNumber(totalConsignado)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Em Importação</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold">{formatNumber(totalImportacao)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Estoque Crítico
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold text-red-600">{criticos}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-estoque"
          />
        </div>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhum produto no estoque" />

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Estoque - {editingItem?.descricao}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estoque Próprio</Label>
                <Input
                  type="number"
                  value={formData.estoque_proprio}
                  onChange={(e) => setFormData({ ...formData, estoque_proprio: parseFloat(e.target.value) || 0 })}
                  data-testid="estoque-proprio"
                />
              </div>
              <div className="space-y-2">
                <Label>Estoque Consignado</Label>
                <Input
                  type="number"
                  value={formData.estoque_consignado}
                  onChange={(e) => setFormData({ ...formData, estoque_consignado: parseFloat(e.target.value) || 0 })}
                  data-testid="estoque-consignado"
                />
              </div>
              <div className="space-y-2">
                <Label>Em Importação</Label>
                <Input
                  type="number"
                  value={formData.estoque_importacao}
                  onChange={(e) => setFormData({ ...formData, estoque_importacao: parseFloat(e.target.value) || 0 })}
                  data-testid="estoque-importacao"
                />
              </div>
              <div className="space-y-2">
                <Label>Reservado</Label>
                <Input
                  type="number"
                  value={formData.estoque_reservado}
                  onChange={(e) => setFormData({ ...formData, estoque_reservado: parseFloat(e.target.value) || 0 })}
                  data-testid="estoque-reservado"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditingItem(null)}>Cancelar</Button>
              <Button type="submit" data-testid="save-estoque-btn">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
